import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LuLogOut, LuShoppingCart } from "react-icons/lu";
import "../styles/Sidebar.css";
import ArrowLeftOrange from "../icons/ArrowLeftOrange";
import BusinessRequestIcon from "../icons/BusinessRequestIcon";
import HealthDashboardIcon from "../icons/HealthDashboardIcon";
import VendorsAvailabilityIcon from "../icons/VendorsAvailabilityIcon";
import UserAnalyticsIcon from "../icons/UserAnalyticsIcon";
import DeliveryHealthIcon from "../icons/DeliveryHealthIcon";
// import ButlerFulfillmentIcon from "../icons/ButlerFulfillmentIcon";
import PromotionIcon from "../icons/PromotionIcon";
import RegionalCampaigns from "../icons/RegionalCampaigns";
import BellIcon from "../icons/BellIcon";
import SliderManegerIcon from "../icons/SliderManegerIcon";
import ComplaintIcon from "../icons/ComplaintIcon";
import LegalDocumentsIcon from "../icons/LegalDocumentsIcon";
import DigitalWalletIcon from "../icons/DigitalWalletIcon";
import { userService } from "../services/userService";
import { translateText, translateLabel, useTranslationSnapshot } from "./../translations/runtime";


const getProfileDetails = () => {
  const payload = userService.getUserPayload() || {};
  const savedProfile = userService.getUserObject() || {};
  const payloadUser = payload.user || {};
  const tokenUsername =
    payload.userName ||
    payload.username ||
    payload.preferred_username ||
    payloadUser.userName ||
    payloadUser.username ||
    "";
  const savedUsername = savedProfile.userName || savedProfile.username || "";
  const isSavedProfileForCurrentUser =
    !tokenUsername ||
    !savedUsername ||
    tokenUsername.toLowerCase() === savedUsername.toLowerCase();
  const fallbackProfile = isSavedProfileForCurrentUser ? savedProfile : {};

  const firstName =
    payload.firstName ||
    payload.firstname ||
    payload.given_name ||
    payloadUser.firstName ||
    payloadUser.firstname ||
    fallbackProfile.firstName ||
    fallbackProfile.firstname ||
    "";
  const lastName =
    payload.lastName ||
    payload.lastname ||
    payload.family_name ||
    payloadUser.lastName ||
    payloadUser.lastname ||
    fallbackProfile.lastName ||
    fallbackProfile.lastname ||
    "";
  const fullName =
    payload.name ||
    payload.fullName ||
    payloadUser.name ||
    payloadUser.fullName ||
    fallbackProfile.name ||
    [firstName, lastName].filter(Boolean).join(" ") ||
    tokenUsername ||
    savedUsername ||
    "User";
  const email =
    payload.email ||
    payload.emailAddress ||
    payload.email_address ||
    payloadUser.email ||
    payloadUser.emailAddress ||
    fallbackProfile.email ||
    "";
  const initials =
    fullName
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((part) => part[0])
      .join("")
      .toUpperCase() || "U";

  return { email, fullName, initials };
};

const NAV_ITEMS = [
  {
    key: "business-request",
    label: "Business Request",
    path: "/vendor-onboarding",
    icon: <BusinessRequestIcon />,
    activeIcon: <BusinessRequestIcon stroke="#ffffff" />,
  },
  {
    key: "health-dashboard",
    label: "Health Dashboard",
    path: "/health-dashboard",
    icon: <HealthDashboardIcon />,
    activeIcon: <HealthDashboardIcon stroke="#ffffff" />,
  },
  {
    key: "vendors",
    label: "Vendors & Availability",
    path: "/vendors",
    icon: <VendorsAvailabilityIcon />,
    activeIcon: <VendorsAvailabilityIcon stroke="#ffffff" />,
  },
  {
    key: "user-analytics",
    label: "User Analytics",
    path: "/user-analytics",
    icon: <UserAnalyticsIcon />,
    activeIcon: <UserAnalyticsIcon stroke="#ffffff" />,
  },
  {
    key: "delivery-health",
    label: "Delivery Health",
    path: "/delivery-health",
    icon: <DeliveryHealthIcon />,
    activeIcon: <DeliveryHealthIcon stroke="#ffffff" />,
  },
  {
    key: "wallet",
    label: "Digital Wallet",
    path: "/digital-wallet",
    icon: <DigitalWalletIcon />,
    activeIcon: <DigitalWalletIcon stroke="#ffffff" />,
  },
  // {
  //   key: "butler",
  //   label: "Butler Fulfillment",
  //   path: "/butler",
  //   icon: <ButlerFulfillmentIcon />,
  //   activeIcon: <ButlerFulfillmentIcon stroke="#ffffff" />,
  // },
];

const GROWTH_ITEMS = [
  {
    key: "promotions",
    label: "Promotions Coverage",
    path: "/promotions",
    icon: <PromotionIcon />,
  },
  {
    key: "campaigns",
    label: "Regional Campaigns",
    path: "/campaigns",
    icon: <RegionalCampaigns />,
  },
  {
    key: "notifications",
    label: "Targeted Notifications",
    path: "/notifications",
    icon: <BellIcon />,
  },
];

const PLATFORM_ITEMS = [
  {
    key: "slider",
    label: "Slider Manager",
    path: "/slider",
    icon: <SliderManegerIcon />,
  },
];

const COMPLIANCE_ITEMS = [
  {
    key: "complaints",
    label: "Complaint Center",
    path: "/complaints",
    icon: <ComplaintIcon />,
  },
  {
    key: "legal",
    label: "Legal Documents",
    path: "/legal",
    icon: <LegalDocumentsIcon />,
  },
  {
    key: "orders",
    label: "Orders",
    path: "/orders",
    icon: <LuShoppingCart />,
  },
];

const MOBILE_SIDEBAR_QUERY = "(max-width: 1024px)";

const isMobileSidebar = () =>
  typeof window !== "undefined" &&
  window.matchMedia(MOBILE_SIDEBAR_QUERY).matches;

function Sidebar({ activeKey }) {
  useTranslationSnapshot();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(() => isMobileSidebar());
  const profile = getProfileDetails();

  const handleLogout = () => {
    userService.logout();
    navigate("/sign-in", { replace: true });
  };

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const mediaQuery = window.matchMedia(MOBILE_SIDEBAR_QUERY);
    const syncSidebar = (matches) => setCollapsed(matches);
    const handleChange = (event) => syncSidebar(event.matches);

    syncSidebar(mediaQuery.matches);

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    }

    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, []);

  const renderItem = (item) => (
    <div
      key={item.key}
      className={`sidebar-item ${activeKey === item.key ? "active" : ""} ${collapsed ? "collapsed" : ""}`}
      onClick={() => {
        if (item.path) navigate(item.path);
        if (isMobileSidebar()) setCollapsed(true);
      }}
      title={translateText(collapsed ? item.label : "")}
    >
      {item.icon && (
        <span className="sidebar-item-icon">
          {activeKey === item.key && item.activeIcon
            ? item.activeIcon
            : item.icon}
        </span>
      )}
      {!collapsed && <span className="sidebar-item-label">{translateText(item.label)}</span>}
    </div>
  );

  return (
    <div className={`sidebar ${collapsed ? "sidebar-collapsed" : ""}`}>
      {/* Collapse arrow */}
      <div className="sidebar-arrow" onClick={() => setCollapsed((p) => !p)}>
        <span
          className={`sidebar-arrow-icon ${collapsed ? "sidebar-arrow-rotated" : ""}`}
        >
          <ArrowLeftOrange />
        </span>
      </div>

      {/* User profile */}
      {!collapsed && (
        <div className="sidebar-profile">
          <div className="sidebar-avatar">{profile.initials}</div>
          <div className="sidebar-user-info">
            <div className="sidebar-user-name">{profile.fullName}</div>
            {profile.email && (
              <div className="sidebar-user-email">{profile.email}</div>
            )}
          </div>
        </div>
      )}
      {collapsed && (
        <div className="sidebar-profile-collapsed">
          <div className="sidebar-avatar">{profile.initials}</div>
        </div>
      )}

      <div className="sidebar-nav">
        {NAV_ITEMS.map(renderItem)}

        {!collapsed && (
          <div className="sidebar-section-label">Growth & Marketing</div>
        )}
        {GROWTH_ITEMS.map(renderItem)}

        {!collapsed && (
          <div className="sidebar-section-label">Platform Configuration</div>
        )}
        {PLATFORM_ITEMS.map(renderItem)}

        {!collapsed && (
          <div className="sidebar-section-label">Operations & Compliance</div>
        )}
        {COMPLIANCE_ITEMS.map(renderItem)}
      </div>

      <button
        className={`sidebar-item sidebar-logout ${collapsed ? "collapsed" : ""}`}
        type="button"
        onClick={handleLogout}
        title={translateText(collapsed ? "Logout" : "")}
      >
        <span className="sidebar-item-icon">
          <LuLogOut aria-hidden="true" />
        </span>
        {!collapsed && <span className="sidebar-item-label">{translateLabel("txt_common_logout", "Logout")}</span>}
      </button>
    </div>
  );
}

export default Sidebar;
